import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import React, { useEffect, useState } from 'react';
import { IoMdArrowBack, IoMdArrowForward } from 'react-icons/io';
import type { Swiper as SwiperType } from 'swiper'; // Import Swiper type
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import { EffectCoverflow, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useWindowDimensions } from '../../utils/hooks';
import { clsx } from '../../utils/utils';
import * as styles from './CenteredCarousel.module.scss';

const MIN_ITEMS_FOR_CAROUSEL = 3;

interface CenteredCarouselProps {
  itemsIdealWidth: number;
  bulletColor?: 'white' | 'primary';
  children: Array<React.ReactElement>;
  onInit?: () => void;
}

function CenteredCarousel({
  itemsIdealWidth,
  bulletColor,
  children,
  onInit,
}: CenteredCarouselProps) {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);

  const windowDimensions = useWindowDimensions();

  const horizontalPadding = windowDimensions && windowDimensions.width >= 1800 ? 260 : 100;

  function getSpaceBetween(windowWidth: number) {
    return gsap.utils.clamp(16, 48, (Math.floor((windowWidth - 360) / itemsIdealWidth) + 1) * 16);
  }

  function getWhiteSpace(windowWidth: number) {
    const spaceBetween = getSpaceBetween(windowWidth);
    const nItemsThatFit = (windowWidth - horizontalPadding) / itemsIdealWidth;
    return horizontalPadding + spaceBetween * (nItemsThatFit - 1);
  }

  const breakpoints = {};
  for (let r = 280; r < 2600; r += 80) {
    breakpoints[r] = {
      slidesPerView: gsap.utils.clamp(1, children.length, (r - getWhiteSpace(r)) / itemsIdealWidth),
      spaceBetween: getSpaceBetween(r),
    };
  }

  let shouldUseSimpleList = false;
  let shouldUseSwiper = false;
  if (windowDimensions) {
    shouldUseSimpleList = !!(
      children.length < MIN_ITEMS_FOR_CAROUSEL ||
      windowDimensions.width - getWhiteSpace(windowDimensions.width) >=
        children.length * itemsIdealWidth
    );
    shouldUseSwiper = !shouldUseSimpleList;
  }

  useEffect(() => {
    if (shouldUseSimpleList || shouldUseSwiper) {
      ScrollTrigger.refresh();

      if (onInit) {
        onInit();
      }
    }
  }, [shouldUseSimpleList, shouldUseSwiper]);

  return (
    <div className={styles.carouselContainer}>
      {shouldUseSimpleList && <div className={styles.itemsSimpleListContainer}>{children}</div>}
      {shouldUseSwiper && (
        <Swiper
          className={clsx(
            styles.swiper,
            swiper?.isLocked && styles.locked,
            bulletColor === 'primary' && styles.primaryColor,
          )}
          onSwiper={setSwiper}
          speed={600}
          slidesPerView={1}
          spaceBetween={16}
          centeredSlides={false}
          breakpoints={breakpoints}
          navigation={{
            prevEl: `.${styles.prevArrow}`,
            nextEl: `.${styles.nextArrow}`,
          }}
          pagination={{
            clickable: true,
            el: `.${styles.paginationContainer}`,
            bulletClass: styles.bullet,
            bulletActiveClass: styles.bulletActive,
          }}
          // onInit={() => {
          //   setTimeout(() => {
          //     ScrollTrigger.refresh();
          //   }, 3000);
          // }}
          modules={[Navigation, Pagination, EffectCoverflow]}
        >
          {React.Children.map(children, (child, i) => (
            <SwiperSlide key={i}>{child}</SwiperSlide>
          ))}
          <div
            className={styles.navigationContainer}
            style={{
              opacity: swiper && swiper.isLocked ? 0 : 1,
            }}
          >
            <div className={clsx(styles.arrow, styles.prevArrow)}>
              <IoMdArrowBack />
            </div>
            <div className={styles.paginationContainer}></div>
            <div className={clsx(styles.arrow, styles.nextArrow)}>
              <IoMdArrowForward />
            </div>
          </div>
        </Swiper>
      )}
    </div>
  );
}

export default CenteredCarousel;
